/**
 * 購入入力モーダル（SPEC §7.2）。
 * 必須は総額のみ。数量・日付は既定値のままでよく、「総額を打つ→保存」で完結する。
 */
import { useState, type ReactNode } from 'react';
import type { ItemRow } from '../../shared/types';
import { today } from '../lib/date';
import { Sheet } from './Sheet';

export interface PurchaseInput {
  qty: number;
  totalPrice: number;
  purchasedAt: string;
  store: string | null;
}

export interface PurchaseModalProps {
  item: ItemRow;
  onClose: () => void;
  onSubmit: (input: PurchaseInput) => void;
}

export function PurchaseModal({ item, onClose, onSubmit }: PurchaseModalProps): ReactNode {
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState('');
  const [purchasedAt, setPurchasedAt] = useState(today);
  const [store, setStore] = useState('');
  const [storeOpen, setStoreOpen] = useState(false);

  const priceValid = /^\d{1,9}$/.test(price);
  const canSave = priceValid && qty >= 1;

  const submit = (): void => {
    if (!canSave) return;
    onSubmit({
      qty,
      totalPrice: Number(price),
      purchasedAt,
      store: store.trim() === '' ? null : store.trim(),
    });
  };

  const unitPrice = priceValid && qty > 0 ? Math.round(Number(price) / qty) : null;

  return (
    <Sheet
      title={`${item.name} を購入`}
      onClose={onClose}
      footer={
        <button type="button" className="btn-primary w-full" disabled={!canSave} onClick={submit}>
          保存
        </button>
      }
    >
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div>
          <label className="mb-1.5 block text-sm font-medium text-zinc-400" htmlFor="purchase-price">
            総額（税込・円）<span className="ml-1 text-orange-400">必須</span>
          </label>
          <input
            id="purchase-price"
            className="field text-2xl font-bold tabular-nums"
            inputMode="numeric"
            pattern="[0-9]*"
            autoFocus
            placeholder="0"
            value={price}
            onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ''))}
          />
          {unitPrice !== null && qty > 1 && (
            <p className="mt-1.5 text-xs text-zinc-500">単価 約 {unitPrice.toLocaleString()} 円</p>
          )}
        </div>

        <div>
          <span className="mb-1.5 block text-sm font-medium text-zinc-400">数量（按分する単位の個数）</span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="btn-ghost size-12 shrink-0 text-xl"
              aria-label="数量を減らす"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
            >
              −
            </button>
            <input
              className="field flex-1 text-center text-xl font-bold tabular-nums"
              inputMode="numeric"
              pattern="[0-9]*"
              value={qty}
              aria-label="数量"
              onChange={(e) => {
                const v = e.target.value.replace(/[^\d]/g, '');
                setQty(v === '' ? 1 : Math.min(9999, Math.max(1, Number(v))));
              }}
            />
            <button
              type="button"
              className="btn-ghost size-12 shrink-0 text-xl"
              aria-label="数量を増やす"
              onClick={() => setQty((q) => Math.min(9999, q + 1))}
            >
              ＋
            </button>
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-zinc-400" htmlFor="purchase-date">
            購入日
          </label>
          {/* type="date" の value は YYYY-MM-DD 文字列。Date へは変換しない（SPEC §2.3） */}
          <input
            id="purchase-date"
            type="date"
            className="field"
            value={purchasedAt}
            onChange={(e) => setPurchasedAt(e.target.value || today())}
          />
        </div>

        <div>
          {storeOpen ? (
            <>
              <label className="mb-1.5 block text-sm font-medium text-zinc-400" htmlFor="purchase-store">
                店舗（任意）
              </label>
              <input
                id="purchase-store"
                className="field"
                value={store}
                placeholder="ドラッグストア など"
                onChange={(e) => setStore(e.target.value)}
              />
            </>
          ) : (
            <button
              type="button"
              className="text-sm text-zinc-500 underline-offset-4 hover:text-zinc-300 hover:underline"
              onClick={() => setStoreOpen(true)}
            >
              ＋ 店舗を入力（任意）
            </button>
          )}
        </div>

        {/* Phase 7 でここにレシート読取ボタンが入る（SPEC §7.2 / §12） */}
        <button type="submit" className="hidden" aria-hidden />
      </form>
    </Sheet>
  );
}
